/**
 * Pure formatters for subscription entitlement values and billing intervals.
 *
 * Extracted from `SubscriptionDetailModal.tsx` per H-ARCH-2 — keeps display
 * logic separate from React component code so it can be unit-tested and
 * reused without pulling in the modal.
 *
 * @see docs/specifications/2026-05-14-tenant-detail-billing-tabs/reviews/architecture-reviewer-review.md
 */
import React from "react";
import { BILLING_INTERVAL_LABELS } from "./subscription-display.constants";

/**
 * AC-23: Returns billing interval display label.
 * For TRIALING subscriptions, returns "Trial {N} dni" using product.metadata.trialDurationDays.
 */
export const getBillingIntervalLabel = (
  status: string,
  billingInterval: string | null | undefined,
  trialDurationDays: number | null | undefined
): string => {
  if (status === "TRIALING") {
    return trialDurationDays != null
      ? `Trial ${trialDurationDays} dni`
      : "Trial (brak danych)";
  }
  if (!billingInterval) return "—";
  return BILLING_INTERVAL_LABELS[billingInterval] ?? billingInterval;
};

/**
 * Formats entitlement value for display in the feature table.
 *
 * - boolean true/false -> badge "Tak"/"Nie"
 * - billingInterval values -> Polish labels
 * - other values -> String(value)
 */
export const formatEntitlementValue = (value: unknown): React.ReactNode => {
  if (value === true || value === false) {
    return (
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
          value
            ? "bg-green-100 text-green-700"
            : "bg-neutral-100 text-neutral-500"
        }`}
      >
        {value ? "Tak" : "Nie"}
      </span>
    );
  }

  if (typeof value === "string" && value in BILLING_INTERVAL_LABELS) {
    return BILLING_INTERVAL_LABELS[value];
  }

  if (value === null || value === undefined) {
    return "—";
  }

  return String(value);
};
